import { useEffect, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { Sparkles } from 'lucide-react'
import { useChangelog } from '@/hooks/use-changelog'
import { type ChangelogTag } from '@/lib/changelog'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { captureEvent } from '@/lib/analytics'

const TAG_VARIANT: Record<ChangelogTag, 'default' | 'success' | 'warning'> = {
  New: 'default',
  Improved: 'success',
  Fixed: 'warning',
}

/**
 * One-time announcement shown when this device has changelog entries it hasn't
 * seen. Auto-opens (after a short delay so it doesn't fight the page mount),
 * is fully dismissible, and never appears during onboarding. Dismissing or
 * opening the full changelog marks everything seen, so it won't nag.
 */
export function WhatsNewModal() {
  const location = useLocation()
  const { unseenEntries, hasUnseen, markSeen } = useChangelog()
  const [open, setOpen] = useState(false)

  const onOnboarding = location.pathname.startsWith('/onboarding')

  useEffect(() => {
    if (!hasUnseen || onOnboarding) return
    const t = setTimeout(() => {
      setOpen(true)
      captureEvent('whats_new_shown', { entries: unseenEntries.map((e) => e.id) })
    }, 600)
    return () => clearTimeout(t)
    // Only depends on whether there's something unseen + the route guard.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnseen, onOnboarding])

  const close = (reason: 'dismiss' | 'view_all') => {
    setOpen(false)
    markSeen()
    captureEvent('whats_new_dismissed', { reason })
  }

  if (!hasUnseen) return null

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close('dismiss') }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary" />
            What’s new in Rundown
          </DialogTitle>
          <DialogDescription>
            We’ve been shipping. Here’s what’s new since you were last here.
          </DialogDescription>
        </DialogHeader>

        <div className="-mr-1 max-h-[55vh] space-y-4 overflow-y-auto pr-1">
          {unseenEntries.map((entry) => {
            const Icon = entry.icon
            return (
              <div key={entry.id} className="rounded-lg border bg-muted/30 p-3">
                <div className="flex items-center gap-2">
                  <Badge variant={TAG_VARIANT[entry.tag]}>{entry.tag}</Badge>
                  <span className="flex items-center gap-1.5 text-sm font-semibold">
                    {Icon && <Icon className="h-4 w-4 text-primary" />}
                    {entry.title}
                  </span>
                </div>
                <p className="mt-1.5 text-sm text-muted-foreground">{entry.summary}</p>
              </div>
            )
          })}
        </div>

        <div className="flex items-center justify-between gap-2">
          <Button asChild variant="ghost" size="sm">
            <Link to="/changelog" onClick={() => close('view_all')}>
              See all updates
            </Link>
          </Button>
          <Button size="sm" onClick={() => close('dismiss')}>
            Got it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
